import { ChevronRightIcon } from '@chakra-ui/icons';
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  Select,
  Text,
  VStack,
} from '@chakra-ui/react';
import { NextPage } from 'next';
import NextHead from 'next/head';
import { useRouter } from 'next/router';
import { ChangeEvent, useCallback, useEffect, useState } from 'react';
import { getPublicRuntimeConfig } from '../../common/config';
import { sendAnalyticsEvent } from '../../common/libs/googleAnalytics';
import { title } from '../../common/libs/metadata';
import {
  SORT_ORDER_LIKE_COUNT_DESC,
  SORT_ORDER_SESSION_COUNT_DESC,
  SORT_ORDER_UPDATE_DATE_DESC,
} from '../../common/libs/sortOrder';
import ArticlesGrid from '../../components/ArticlesGrid';
import DepartmentsGrid from '../../components/DepartmentsGrid';
import { JsonLd } from '../../components/JsonLd';
import TargetReaderSelect from '../../components/TargetReaderSelect';
import useApiClient from '../../hooks/useApiClient';
import { useIntersectionObserver } from '../../hooks/useIntersectionObserver';
import { Props as ServerSideProps } from '../../server/serverSideProps/department/departmentId';

export { getServerSideProps } from '../../server/serverSideProps/department/departmentId';

type Department = ServerSideProps['currentDepartment'];

function JsonLdBreadcrumbList({ department }: { department: Department }) {
  const { slideWebBaseUrl } = getPublicRuntimeConfig();

  const structuredData = {
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: 'ホーム',
        item: slideWebBaseUrl,
      },
      {
        '@type': 'ListItem',
        position: 2,
        name: `「${department.name}」のスライド`,
        item: `${slideWebBaseUrl}/department/${department.id}`,
      },
    ],
  };

  return <JsonLd data={structuredData} />;
}

function PageHead({ department }: { department: Department }) {
  const { slideWebBaseUrl } = getPublicRuntimeConfig();
  const canonicalUrl = `${slideWebBaseUrl}/department/${department.id}`;

  return (
    <>
      <NextHead>
        <title>{title(`「${department.name}」のスライド一覧`)}</title>
        <link rel="canonical" href={canonicalUrl} />
      </NextHead>
      <JsonLdBreadcrumbList department={department} />
    </>
  );
}

/**
 * パンくずリスト
 */
function Breadcrumbs({ department }: { department: Department }) {
  return (
    <Breadcrumb
      spacing="8px"
      separator={<ChevronRightIcon color="gray.500" />}
      fontSize="xs"
      opacity="0.8"
    >
      <BreadcrumbItem>
        <BreadcrumbLink
          href="/"
          onClick={() => sendAnalyticsEvent('breadcrumb', 'click', 'home')}
        >
          ホーム
        </BreadcrumbLink>
      </BreadcrumbItem>
      <BreadcrumbItem isCurrentPage>
        <BreadcrumbLink
          href={`/department/${department.id}`}
          onClick={() =>
            sendAnalyticsEvent('breadcrumb', 'click', 'department')
          }
          cursor="default"
          textDecoration="underline"
        >
          「{department.name}」のスライド
        </BreadcrumbLink>
      </BreadcrumbItem>
    </Breadcrumb>
  );
}

/**
 * 診療科画面
 */
const DepartmentPage: NextPage<ServerSideProps> = ({
  currentDepartment,
  articles,
  totalCount,
  sortOrder,
  departments,
  readerTypeIds,
  targetReaderTypes,
  isMobile,
}) => {
  const router = useRouter();
  const { apiClient } = useApiClient();

  const [isLoading, setIsLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [renderArticles, setRenderArticles] = useState(articles);

  useEffect(() => {
    setRenderArticles(articles);
    setPage(1);
    setHasMore(true);
  }, [articles]);

  const onSelectChange = useCallback(
    (e: ChangeEvent<HTMLSelectElement>) => {
      const additionalQueryParam =
        readerTypeIds && readerTypeIds.length > 0
          ? `&readerTypeIds=${JSON.stringify(readerTypeIds)}`
          : '';
      router.push(
        `/department/${currentDepartment.id}?sortOrder=${e.target.value}${additionalQueryParam}`,
      );
    },
    [readerTypeIds, router, currentDepartment.id],
  );

  const targetReaderTypesForView = targetReaderTypes.map(
    (targetReaderType) => ({
      ...targetReaderType,
      selected: readerTypeIds.includes(targetReaderType.id),
    }),
  );

  const onTargetReadersChange = useCallback(
    (readerTypeIds: number[]) => {
      const additionalQueryParam =
        readerTypeIds.length > 0
          ? `&readerTypeIds=${JSON.stringify(readerTypeIds)}`
          : '';
      router.push(
        `/department/${currentDepartment.id}?sortOrder=${sortOrder}${additionalQueryParam}`,
      );
    },
    [currentDepartment.id, router, sortOrder],
  );

  const onClickTargetReaders = (event: ChangeEvent<HTMLInputElement>) => {
    const targetReaderId = Number.parseInt(event.target.id);
    let modifiedReaderTypeIds;
    if (readerTypeIds.includes(targetReaderId)) {
      modifiedReaderTypeIds = readerTypeIds.filter((s) => s !== targetReaderId);
    } else {
      modifiedReaderTypeIds = [...readerTypeIds, targetReaderId];
    }
    onTargetReadersChange(modifiedReaderTypeIds);
  };

  const loadMore = useCallback(async () => {
    if (isLoading || !hasMore) return;
    setIsLoading(true);
    const nextPage = page + 1;
    try {
      const res = await apiClient(
        `/api/articles?page=${nextPage}&readerTypeIds=${JSON.stringify(
          readerTypeIds,
        )}&sortOrder=${sortOrder}&departmentId=${currentDepartment.id}`,
        {
          method: 'GET',
        },
      );
      if (res.ok) {
        const data = await res.json();
        setPage(nextPage);
        setHasMore(data.hasMore);
        if (data.articles.length > 0) {
          setRenderArticles((v) => [...v, ...data.articles]);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setIsLoading(false);
    }
  }, [
    apiClient,
    currentDepartment.id,
    hasMore,
    isLoading,
    page,
    readerTypeIds,
    sortOrder,
  ]);
  const intersectionRef = useIntersectionObserver(loadMore);

  return (
    <>
      <PageHead department={currentDepartment} />
      <VStack spacing={4} align="flex-start">
        <Breadcrumbs department={currentDepartment} />
        <Heading as="h1" fontSize="xl">
          {`「${currentDepartment.name}」のスライド一覧`}
        </Heading>
        <Flex
          justify="space-between"
          w="full"
          wrap="wrap"
          alignItems="center"
          gap={2}
        >
          <TargetReaderSelect
            targetReaderTypes={targetReaderTypesForView}
            onChange={onClickTargetReaders}
          />
          <FormControl
            as={Flex}
            w="auto"
            align="center"
            justifyContent="flex-end"
          >
            <FormLabel m={0}>表示順：</FormLabel>
            <Select
              w="200px"
              h="3em"
              fontSize="sm"
              value={sortOrder}
              onChange={onSelectChange}
              onFocus={() => sendAnalyticsEvent('slide', 'sort', null)}
            >
              <option value={SORT_ORDER_UPDATE_DATE_DESC}>
                公開日／更新日（新しい順）
              </option>
              <option value={SORT_ORDER_SESSION_COUNT_DESC}>
                ページ閲覧数（多い順）
              </option>
              <option value={SORT_ORDER_LIKE_COUNT_DESC}>
                保存済み数（多い順）
              </option>
            </Select>
          </FormControl>
        </Flex>
        <Text mb={8}>検索結果: {totalCount}件</Text>
        <ArticlesGrid
          articles={renderArticles}
          gaEventLabel="from_department"
          isMobile={isMobile}
          onShowThumbnail={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'show',
              `department_${currentDepartment.id}#${articleId}`,
            );
          }}
          onClick={(articleId) => {
            sendAnalyticsEvent(
              'slide_thumbnail',
              'click',
              `department_${currentDepartment.id}#${articleId}`,
            );
          }}
        />
        <div ref={intersectionRef} />
        <Heading as="h2" fontSize="xl" mt={8} mb={4}>
          他の診療科のスライド
        </Heading>
        <DepartmentsGrid
          departments={departments}
          gaEventLabel="from_department"
        />
      </VStack>
    </>
  );
};

export default DepartmentPage;
